import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { 
  Mountain, Filter, Search, AlertTriangle, Clock,
  MapPin, TrendingUp, Route, Loader2, X, ChevronRight,
  Bike, Car, Navigation, RotateCcw, Ticket
} from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { useLocation } from '@/contexts/LocationContext';
import { api } from '@/lib/api';
import { getDistanceKm } from '@/lib/distance';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { toast } from 'sonner';
import SEO from '@/components/SEO';

const TrailsPage = () => {
  const { language } = useLanguage();
  const { location } = useLocation();
  const [trails, setTrails] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [weatherAlerts, setWeatherAlerts] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('Sendero'); // Sendero, Bicicletas y caballos, Vehículos a motor
  const [showFeaturedOnly, setShowFeaturedOnly] = useState(true); // Show featured by default
  const [expandedAlert, setExpandedAlert] = useState(null); // For alert pill expansion
  const [filters, setFilters] = useState({
    difficulty: '',
    modality: '',
    municipality: '',
    natural_space: ''
  });
  const [municipalities, setMunicipalities] = useState([]);
  const [naturalSpaces, setNaturalSpaces] = useState([]);
  const [showFilters, setShowFilters] = useState(false);

  const labels = {
    es: {
      title: 'Senderos de Tenerife',
      subtitle: 'Red oficial de senderos del Cabildo',
      search: 'Buscar sendero...',
      filters: 'Filtros',
      allDifficulties: 'Todas las dificultades',
      allModalities: 'Todas las modalidades',
      allMunicipalities: 'Todos los municipios',
      allNaturalSpaces: 'Todos los espacios',
      difficulty: 'Dificultad',
      modality: 'Modalidad',
      municipality: 'Municipio',
      naturalSpace: 'Espacio natural',
      easy: 'Fácil',
      moderate: 'Moderada',
      hard: 'Difícil',
      circular: 'Circular',
      lineal: 'Lineal',
      km: 'km',
      hours: 'h',
      noResults: 'No se encontraron senderos',
      clearFilters: 'Limpiar filtros',
      alerts: 'Alertas activas',
      viewOnTenerifeOn: 'Ver en TenerifeON',
      totalTrails: 'senderos',
      activeAlerts: 'alertas activas',
      nearby: 'cerca de ti',
      tabSenderos: 'A pie',
      tabBici: 'Bici / Caballo',
      tabVehiculos: 'Vehículos',
      apply: 'Aplicar',
      requiresPermit: 'Requiere permiso',
      requestPermit: 'Solicitar permiso'
    },
    en: {
      title: 'Tenerife Trails',
      subtitle: 'Official trail network of the Cabildo',
      search: 'Search trail...',
      filters: 'Filters',
      allDifficulties: 'All difficulties',
      allModalities: 'All modalities',
      allMunicipalities: 'All municipalities',
      allNaturalSpaces: 'All natural spaces',
      difficulty: 'Difficulty',
      modality: 'Modality',
      municipality: 'Municipality',
      naturalSpace: 'Natural space',
      easy: 'Easy',
      moderate: 'Moderate',
      hard: 'Hard',
      circular: 'Circular',
      lineal: 'Linear',
      km: 'km',
      hours: 'h',
      noResults: 'No trails found',
      clearFilters: 'Clear filters',
      alerts: 'Active alerts',
      viewOnTenerifeOn: 'View on TenerifeON',
      totalTrails: 'trails',
      activeAlerts: 'active alerts',
      nearby: 'near you',
      tabSenderos: 'Hiking',
      tabBici: 'Bike / Horse',
      tabVehiculos: 'Vehicles',
      apply: 'Apply',
      requiresPermit: 'Permit required',
      requestPermit: 'Request permit'
    },
    de: {
      title: 'Wanderwege Teneriffa',
      subtitle: 'Offizielles Wegenetz des Cabildo',
      search: 'Wanderweg suchen...',
      filters: 'Filter',
      allDifficulties: 'Alle Schwierigkeiten',
      allModalities: 'Alle Modalitäten',
      allMunicipalities: 'Alle Gemeinden',
      allNaturalSpaces: 'Alle Naturräume',
      difficulty: 'Schwierigkeit',
      modality: 'Modalität',
      municipality: 'Gemeinde',
      naturalSpace: 'Naturraum',
      easy: 'Leicht',
      moderate: 'Mittel',
      hard: 'Schwer',
      circular: 'Rundweg',
      lineal: 'Strecke',
      km: 'km',
      hours: 'h',
      noResults: 'Keine Wanderwege gefunden',
      clearFilters: 'Filter löschen',
      alerts: 'Aktive Warnungen',
      viewOnTenerifeOn: 'Auf TenerifeON ansehen',
      totalTrails: 'Wege',
      activeAlerts: 'aktive Warnungen',
      nearby: 'in deiner Nähe',
      tabSenderos: 'Wandern',
      tabBici: 'Rad / Pferd',
      tabVehiculos: 'Fahrzeuge',
      apply: 'Anwenden',
      requiresPermit: 'Genehmigung erforderlich',
      requestPermit: 'Genehmigung beantragen'
    }
  };

  const t = labels[language] || labels.es;

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    loadTrails();
    // searchQuery se aplica al enviar el formulario, no en cada tecla
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, filters, showFeaturedOnly]);

  const loadData = async () => {
    try {
      const [municipalitiesRes, naturalSpacesRes, alertsRes, weatherRes, statsRes] = await Promise.all([
        api.getTrailMunicipalities(),
        api.getTrailNaturalSpaces(),
        api.getActiveTrailAlerts(),
        api.getWeatherAlerts(),
        api.getTrailStats()
      ]);
      setMunicipalities(municipalitiesRes.data);
      setNaturalSpaces(naturalSpacesRes.data);
      setAlerts(alertsRes.data);
      setWeatherAlerts(weatherRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error loading trail data:', error);
    }
  };

  const loadTrails = async () => {
    setLoading(true);
    try {
      const params = { 
        trail_class: activeTab,
        ...filters, 
        limit: 200 
      };
      if (searchQuery) params.search = searchQuery;
      if (showFeaturedOnly) params.featured = true;
      
      const res = await api.getTrails(params);
      setTrails(res.data);
    } catch (error) {
      console.error('Error loading trails:', error);
      toast.error('Error al cargar senderos');
    } finally {
      setLoading(false);
    }
  };

  // Calculate distances client-side - only numbers update, no data reload
  const trailsWithDistance = useMemo(() => {
    if (!trails.length) return [];
    return trails.map(t => ({
      ...t,
      user_distance: (location?.lat && t.start_latitude) 
        ? parseFloat(getDistanceKm(location.lat, location.lng, t.start_latitude, t.start_longitude).toFixed(1))
        : t.user_distance
    })).sort((a, b) => (a.user_distance ?? 999) - (b.user_distance ?? 999));
  }, [trails, location]);

  const handleSearch = (e) => {
    e.preventDefault();
    loadTrails();
  };

  const clearFilters = () => {
    setFilters({
      difficulty: '',
      modality: '',
      municipality: '',
      natural_space: ''
    });
    setSearchQuery('');
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'Fácil': return 'bg-green-100 text-green-700 border-green-200';
      case 'Moderada': return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      case 'Difícil': return 'bg-red-100 text-red-700 border-red-200';
      default: return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const getModalityIcon = (modality) => {
    if (modality?.toLowerCase().includes('circular')) {
      return <RotateCcw size={12} className="text-blue-500" />;
    }
    return null;
  };

  const formatDistance = (meters) => {
    if (meters >= 1000) {
      return `${(meters / 1000).toFixed(1)} ${t.km}`;
    }
    return `${Math.round(meters)} m`;
  };

  const formatUserDistance = (km) => {
    if (km === undefined || km === null) return null;
    if (km < 1) return `${Math.round(km * 1000)} m`;
    return `${km.toFixed(1)} km`;
  };

  const getAlertField = (alert, field) => {
    if (language === 'en' && alert[`${field}_en`]) return alert[`${field}_en`];
    if (language === 'de' && alert[`${field}_de`]) return alert[`${field}_de`];
    return alert[field];
  };

  const getDifficultyLabel = (difficulty) => {
    const labels = {
      'Fácil': { en: 'Easy', de: 'Leicht' },
      'Moderada': { en: 'Moderate', de: 'Mittel' },
      'Difícil': { en: 'Hard', de: 'Schwer' },
    };
    if (language !== 'es' && labels[difficulty]?.[language]) return labels[difficulty][language];
    return difficulty;
  };

  const getModalityLabel = (modality) => {
    if (!modality) return modality;
    const labels = {
      'Circular': { en: 'Circular', de: 'Rundweg' },
      'Lineal': { en: 'Linear', de: 'Strecke' },
    };
    if (language !== 'es' && labels[modality]?.[language]) return labels[modality][language];
    return modality;
  };

  const getTrailAlertMessage = (trail) => {
    if (language === 'en' && trail.alert_message_en) return trail.alert_message_en;
    if (language === 'de' && trail.alert_message_de) return trail.alert_message_de;
    return trail.alert_message;
  };

  const hasActiveFilters = Object.values(filters).some(v => v) || searchQuery;

  const tabs = [
    { key: 'Sendero', label: t.tabSenderos, icon: Mountain, count: stats?.senderos || 0 },
    { key: 'Bicicletas y caballos', label: t.tabBici, icon: Bike, count: stats?.bicicletas_caballos || 0 },
    { key: 'Vehículos a motor', label: t.tabVehiculos, icon: Car, count: stats?.vehiculos_motor || 0 },
  ];

  return (
    <div className="pb-20 bg-[#FDFBF7]" data-testid="trails-page">
      <SEO
        title={language === 'en' ? 'Tenerife Hiking Trails' : language === 'de' ? 'Wanderwege Teneriffa' : 'Senderos de Tenerife'}
        description={language === 'en' ? '224 official trails in Tenerife: hiking, biking and vehicle routes. Maps, elevation profiles, difficulty ratings and real-time alerts.' : language === 'de' ? '224 offizielle Wege auf Teneriffa: Wandern, Radfahren und Fahrzeugpisten. Karten, Hohenprofile und Echtzeit-Warnungen.' : '224 senderos oficiales de Tenerife: senderismo, bici y vehiculos. Mapas, perfiles de alturas, dificultad y alertas en tiempo real.'}
        path="/trails"
        lang={language}
      />
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-[#38B2AC] to-[#319795] text-white">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center gap-3 mb-1">
            <Mountain size={24} />
            <h1 className="text-xl font-bold">{t.title}</h1>
          </div>
          <p className="text-white/80 text-sm mb-3">{t.subtitle}</p>
          
          {/* Stats */}
          {stats && (
            <div className="flex gap-3 text-sm">
              <div className="bg-white/20 rounded-lg px-3 py-1.5">
                <span className="font-bold">{stats.total_imported}</span> {t.totalTrails}
              </div>
              {alerts.length > 0 && (
                <div className="bg-amber-400/30 rounded-lg px-3 py-1.5 flex items-center gap-1">
                  <AlertTriangle size={14} />
                  <span className="font-bold">{alerts.length}</span> {t.activeAlerts}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Tabs by Type */}
      <div className="bg-white border-b sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === tab.key
                      ? 'border-[#38B2AC] text-[#319795]'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                  data-testid={`tab-${tab.key}`}
                >
                  <Icon size={16} />
                  <span className="hidden sm:inline">{tab.label}</span>
                  <span className="text-xs bg-gray-100 px-1.5 py-0.5 rounded-full">
                    {tab.count}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Weather/Disaster Alerts - Compact pill format */}
      {weatherAlerts.length > 0 && (
        <div className="bg-white border-b" data-testid="weather-alerts-banner">
          <div className="max-w-7xl mx-auto px-4 py-2">
            <div className="flex flex-wrap gap-2">
              {weatherAlerts.slice(0, 3).map((alert) => (
                <button
                  key={alert.id}
                  onClick={() => setExpandedAlert(expandedAlert === alert.id ? null : alert.id)}
                  className="inline-flex items-center gap-2 px-3 py-1.5 bg-red-100 text-red-700 rounded-full text-sm font-medium hover:bg-red-200 transition-colors"
                >
                  <AlertTriangle size={14} />
                  <span className="truncate max-w-[200px]">{getAlertField(alert, 'title')}</span>
                </button>
              ))}
            </div>
            {/* Expanded alert detail */}
            {expandedAlert && weatherAlerts.find(a => a.id === expandedAlert) && (() => {
              const alert = weatherAlerts.find(a => a.id === expandedAlert);
              return (
                <div className="mt-3 p-3 bg-red-50 rounded-lg border border-red-200">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <h4 className="font-bold text-red-800 text-sm">
                        {getAlertField(alert, 'title')}
                      </h4>
                      <p className="text-red-700 text-xs mt-1">
                        {getAlertField(alert, 'description')}
                      </p>
                      <a 
                        href={alert.source_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-red-500 text-xs mt-2 inline-block hover:underline"
                      >
                        {language === 'es' ? 'Ver fuente oficial' : language === 'de' ? 'Offizielle Quelle' : 'Official source'} →
                      </a>
                    </div>
                    <button 
                      onClick={() => setExpandedAlert(null)}
                      className="p-1 hover:bg-red-200 rounded"
                    >
                      <X size={16} className="text-red-600" />
                    </button>
                  </div>
                </div>
              );
            })()}
          </div>
        </div>
      )}

      {/* Search & Filters */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 py-3">
          {/* Featured / All toggle */}
          <div className="flex gap-2 mb-3">
            <button
              onClick={() => setShowFeaturedOnly(true)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                showFeaturedOnly 
                  ? 'bg-[#38B2AC] text-white' 
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
              data-testid="featured-trails-btn"
            >
              ⭐ {language === 'es' ? 'Destacadas' : language === 'de' ? 'Empfohlen' : 'Featured'}
            </button>
            <button
              onClick={() => setShowFeaturedOnly(false)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                !showFeaturedOnly 
                  ? 'bg-[#38B2AC] text-white' 
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
              data-testid="all-trails-btn"
            >
              {language === 'es' ? 'Ver todas' : language === 'de' ? 'Alle anzeigen' : 'View all'}
            </button>
          </div>
          
          <form onSubmit={handleSearch} className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
              <Input
                type="text"
                placeholder={t.search}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-white border-gray-200"
                data-testid="trails-search-input"
              />
            </div>
            <Sheet open={showFilters} onOpenChange={setShowFilters}>
              <SheetTrigger asChild>
                <Button variant="outline" className="gap-2 relative">
                  <Filter size={16} />
                  {t.filters}
                  {hasActiveFilters && (
                    <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-[#38B2AC]"></span>
                  )}
                </Button>
              </SheetTrigger>
              <SheetContent side="bottom" className="h-[70vh]">
                <SheetHeader>
                  <SheetTitle>{t.filters}</SheetTitle>
                </SheetHeader>
                <div className="space-y-4 mt-4">
                  {/* Modality (only for Sendero) */}
                  {activeTab === 'Sendero' && (
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-2 block">{t.modality}</label>
                      <Select
                        value={filters.modality}
                        onValueChange={(v) => setFilters(prev => ({ ...prev, modality: v === 'all' ? '' : v }))}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder={t.allModalities} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">{t.allModalities}</SelectItem>
                          <SelectItem value="Circular">{t.circular}</SelectItem>
                          <SelectItem value="Lineal">{t.lineal}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {/* Difficulty */}
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-2 block">{t.difficulty}</label>
                    <Select
                      value={filters.difficulty}
                      onValueChange={(v) => setFilters(prev => ({ ...prev, difficulty: v === 'all' ? '' : v }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={t.allDifficulties} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">{t.allDifficulties}</SelectItem>
                        <SelectItem value="Fácil">{t.easy}</SelectItem>
                        <SelectItem value="Moderada">{t.moderate}</SelectItem>
                        <SelectItem value="Difícil">{t.hard}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Municipality */}
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-2 block">{t.municipality}</label>
                    <Select
                      value={filters.municipality}
                      onValueChange={(v) => setFilters(prev => ({ ...prev, municipality: v === 'all' ? '' : v }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={t.allMunicipalities} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">{t.allMunicipalities}</SelectItem>
                        {municipalities.map((m) => (
                          <SelectItem key={m} value={m}>{m}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Natural Space */}
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-2 block">{t.naturalSpace}</label>
                    <Select
                      value={filters.natural_space}
                      onValueChange={(v) => setFilters(prev => ({ ...prev, natural_space: v === 'all' ? '' : v }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={t.allNaturalSpaces} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">{t.allNaturalSpaces}</SelectItem>
                        {naturalSpaces.map((s) => (
                          <SelectItem key={s} value={s}>{s}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex gap-2 pt-4">
                    <Button variant="outline" className="flex-1" onClick={clearFilters}>
                      {t.clearFilters}
                    </Button>
                    <Button className="flex-1 bg-[#38B2AC] hover:bg-[#319795]" onClick={() => setShowFilters(false)}>
                      {t.apply}
                    </Button>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </form>

          {/* Active Filter Pills */}
          {hasActiveFilters && (
            <div className="flex flex-wrap gap-2 mt-2">
              {filters.modality && (
                <Badge variant="secondary" className="bg-[#38B2AC]/10 text-[#319795]">
                  {filters.modality}
                  <button onClick={() => setFilters(prev => ({ ...prev, modality: '' }))} className="ml-1">
                    <X size={12} />
                  </button>
                </Badge>
              )}
              {filters.difficulty && (
                <Badge variant="secondary" className="bg-[#38B2AC]/10 text-[#319795]">
                  {filters.difficulty}
                  <button onClick={() => setFilters(prev => ({ ...prev, difficulty: '' }))} className="ml-1">
                    <X size={12} />
                  </button>
                </Badge>
              )}
              {filters.municipality && (
                <Badge variant="secondary" className="bg-[#38B2AC]/10 text-[#319795]">
                  {filters.municipality}
                  <button onClick={() => setFilters(prev => ({ ...prev, municipality: '' }))} className="ml-1">
                    <X size={12} />
                  </button>
                </Badge>
              )}
              <button onClick={clearFilters} className="text-xs text-gray-500 hover:text-[#38B2AC]">
                {t.clearFilters}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Location indicator */}
      {location?.lat && (
        <div className="max-w-7xl mx-auto px-4 py-2">
          <div className="flex items-center gap-1 text-xs text-gray-500">
            <Navigation size={12} className="text-[#38B2AC]" />
            <span>{language === 'es' ? 'Ordenados por cercanía a tu ubicación' : language === 'de' ? 'Sortiert nach Entfernung' : 'Sorted by distance from you'}</span>
          </div>
        </div>
      )}

      {/* Trails List */}
      <div className="max-w-7xl mx-auto px-4 py-3">
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm text-gray-500">
            {trailsWithDistance.length} {t.totalTrails}
          </span>
        </div>

        {loading ? (
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => (
              <Skeleton key={i} className="h-24 rounded-xl" />
            ))}
          </div>
        ) : trailsWithDistance.length === 0 ? (
          <div className="text-center py-12">
            <Mountain size={48} className="mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500">{t.noResults}</p>
            <Button variant="link" className="text-[#38B2AC] mt-2" onClick={clearFilters}>
              {t.clearFilters}
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            {trailsWithDistance.map((trail) => (
              <Link
                key={trail.id}
                to={`/trails/${trail.id}`}
                className={`block bg-white rounded-xl border p-4 hover:shadow-md transition-shadow ${
                  trail.has_alert ? 'border-amber-200' : trail.requires_permit ? 'border-purple-200' : 'border-gray-100'
                }`}
                data-testid={`trail-card-${trail.id}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    {/* Header */}
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      {trail.matricula && (
                        <span className="text-xs font-mono bg-gray-100 px-1.5 py-0.5 rounded">
                          {trail.matricula}
                        </span>
                      )}
                      {trail.modality && getModalityIcon(trail.modality) && (
                        <span className="flex items-center gap-1 text-xs text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded">
                          {getModalityIcon(trail.modality)}
                          {getModalityLabel(trail.modality)}
                        </span>
                      )}
                      {trail.requires_permit && (
                        <span className="flex items-center gap-1 text-xs text-purple-600 bg-purple-50 px-1.5 py-0.5 rounded font-medium">
                          <Ticket size={12} />
                          {t.requiresPermit}
                        </span>
                      )}
                      {trail.has_alert && (
                        <AlertTriangle size={14} className="text-amber-500" />
                      )}
                    </div>
                    
                    {/* Name */}
                    <h3 className="font-medium text-gray-900 line-clamp-2">
                      {trail.name}
                    </h3>
                    
                    {/* Location */}
                    {trail.municipalities?.length > 0 && (
                      <p className="text-xs text-gray-500 mt-0.5 flex items-center gap-1">
                        <MapPin size={10} />
                        {trail.municipalities.slice(0, 2).join(', ')}
                        {trail.municipalities.length > 2 && ` +${trail.municipalities.length - 2}`}
                      </p>
                    )}
                    
                    {/* Stats */}
                    <div className="flex flex-wrap items-center gap-3 mt-2 text-xs text-gray-600">
                      <span className="flex items-center gap-1">
                        <Route size={12} />
                        {formatDistance(trail.distance_meters)}
                      </span>
                      {trail.duration_hours && (
                        <span className="flex items-center gap-1">
                          <Clock size={12} />
                          ~{trail.duration_hours} {t.hours}
                        </span>
                      )}
                      <span className="flex items-center gap-1">
                        <TrendingUp size={12} />
                        +{Math.round(trail.elevation_gain)}m
                      </span>
                      {trail.user_distance !== undefined && trail.user_distance !== null && (
                        <span className="flex items-center gap-1 text-[#38B2AC] font-medium">
                          <Navigation size={12} />
                          {formatUserDistance(trail.user_distance)} {t.nearby}
                        </span>
                      )}
                    </div>
                  </div>
                  
                  {/* Right side */}
                  <div className="flex flex-col items-end gap-2">
                    <Badge variant="outline" className={getDifficultyColor(trail.difficulty)}>
                      {getDifficultyLabel(trail.difficulty)}
                    </Badge>
                    <ChevronRight size={18} className="text-gray-300" />
                  </div>
                </div>
                
                {/* Alert message */}
                {trail.has_alert && trail.alert_message && (
                  <div className="mt-2 p-2 bg-amber-50 rounded-lg text-xs text-amber-700 flex items-center gap-2">
                    <AlertTriangle size={12} />
                    <span className="line-clamp-1">{getTrailAlertMessage(trail)}</span>
                  </div>
                )}
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default TrailsPage;
